// Admin hub landing page: KPI overview (with deltas vs the previous window),
// calls/sentiment charts, top companions and recent calls.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getGuideAnalytics,
  getOverview,
  getTimeseries,
  listCalls,
  type AnalyticsOverview,
  type CallRecord,
  type GuideAnalyticsRow,
  type OverviewWeight,
  type TimeseriesPoint,
} from "../services/analytics";
import { CallsBarChart, SentimentLineChart } from "../components/analytics/Charts";
import { scoreTone } from "../components/evals/RubricSummary";
import {
  EngineSelect,
  InputModeToggle,
  RangePicker,
  RecentCallsList,
  SentimentCell,
  TrafficToggle,
} from "../components/analytics/bits";
import {
  apiEngine,
  apiInputMode,
  apiSource,
  fillDays,
  formatDateTime,
  formatDurationMs,
  formatSentiment,
  parseEngineParam,
  parseSourceParam,
  pctDelta,
  previousWindow,
  windowFromPreset,
  type EngineFilter,
  type InputModeFilter,
  type RangePreset,
  type SourceFilter,
} from "../components/analytics/format";

function Delta({
  value,
  suffix = "%",
  invert = false,
}: {
  value: number | null;
  suffix?: string;
  // For lower-is-better metrics (early drops, flagged, disengagement):
  // swaps the green/red coloring while the arrow keeps the actual sign.
  invert?: boolean;
}) {
  if (value == null) return <span className="kpi-delta muted">—</span>;
  const improved = invert ? value < 0 : value > 0;
  const dir = value === 0 ? "flat" : improved ? "up" : "down";
  const rendered = suffix === "%" ? `${Math.abs(value).toFixed(0)}%` : Math.abs(value).toFixed(2);
  return (
    <span className={`kpi-delta ${dir}`}>
      {value > 0 ? "▲" : value < 0 ? "▼" : "▬"} {rendered}
    </span>
  );
}

function KpiCard({
  label,
  value,
  delta,
  hint,
}: {
  label: string;
  value: string;
  delta?: React.ReactNode;
  // Hover explanation — every quality metric must say what it measures and
  // which direction is good, or the number is noise to a new reader.
  hint?: string;
}) {
  return (
    <div className="kpi-card" title={hint}>
      <div className="kpi-label">
        {label}
        {hint && <span className="muted"> ⓘ</span>}
      </div>
      <div className="kpi-value">{value}</div>
      {delta !== undefined && <div className="kpi-foot">{delta} <span className="muted">vs prev.</span></div>}
    </div>
  );
}

function formatRate(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${Math.round(value * 100)}%`;
}

function formatNumber(value: number | null | undefined, digits = 1): string {
  return value === null || value === undefined ? "—" : value.toFixed(digits);
}

// Percent change of a rate between windows; null when either side is missing.
function rateDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
): number | null {
  return current != null && previous != null ? pctDelta(current, previous) : null;
}

const WEIGHT_OPTIONS: { key: OverviewWeight; label: string }[] = [
  { key: "pooled", label: "All" },
  { key: "user", label: "Per user" },
];

// Conversation metrics can be pooled (every conversation counts once) or
// averaged per user first — a handful of heavy users produce half the traffic.
function WeightToggle({
  value,
  onChange,
}: {
  value: OverviewWeight;
  onChange: (weight: OverviewWeight) => void;
}) {
  return (
    <div
      className="seg"
      role="group"
      aria-label="Metric weighting"
      title="All: every conversation counts once. Per user: each user's average first, then averaged across users."
    >
      {WEIGHT_OPTIONS.map((w) => (
        <button
          key={w.key}
          className={value === w.key ? "active" : ""}
          onClick={() => onChange(w.key)}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [preset, setPreset] = useState<RangePreset>("30d");
  const [mode, setMode] = useState<InputModeFilter>("all");
  const [weight, setWeight] = useState<OverviewWeight>("pooled");
  // Traffic source lives in the URL (?source=studio_bench|all) so filtered
  // views are shareable; "app" is the default and keeps the URL clean.
  const traffic = parseSourceParam(searchParams.get("source"));
  // So is the voice engine (?engine=gemini_live), shared with the Calls page.
  const engine = parseEngineParam(searchParams.get("engine"));
  const setParam = (key: string, value: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };
  const setTraffic = (s: SourceFilter) => setParam("source", s === "app" ? null : s);
  const setEngine = (e: EngineFilter) => setParam("engine", e === "all" ? null : e);

  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [prevOverview, setPrevOverview] = useState<AnalyticsOverview | null>(null);
  const [guides, setGuides] = useState<GuideAnalyticsRow[]>([]);
  const [series, setSeries] = useState<TimeseriesPoint[]>([]);
  const [recent, setRecent] = useState<CallRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const win = useMemo(() => windowFromPreset(preset), [preset]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const m = apiInputMode(mode);
    const src = apiSource(traffic);
    const eng = apiEngine(engine);
    const prev = previousWindow(win);
    Promise.allSettled([
      getOverview({ from: win.from, to: win.to, input_mode: m, source: src, engine: eng, weight }),
      getOverview({ from: prev.from, to: prev.to, input_mode: m, source: src, engine: eng, weight }),
      getGuideAnalytics({ from: win.from, to: win.to, input_mode: m, source: src, engine: eng }),
      getTimeseries({ from: win.from, to: win.to, input_mode: m, source: src, engine: eng }),
      listCalls({ from: win.from, to: win.to, input_mode: m, source: src, engine: eng, limit: 5 }),
    ]).then(([ov, prevOv, gu, ts, calls]) => {
      if (cancelled) return;
      setOverview(ov.status === "fulfilled" ? ov.value : null);
      setPrevOverview(prevOv.status === "fulfilled" ? prevOv.value : null);
      setGuides(gu.status === "fulfilled" ? gu.value.items : []);
      setSeries(ts.status === "fulfilled" ? ts.value.items : []);
      setRecent(calls.status === "fulfilled" ? calls.value.items : []);
      if (ov.status === "rejected") {
        const reason = ov.reason;
        setError(reason instanceof Error ? reason.message : "Analytics service unavailable");
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [win, mode, traffic, engine, weight]);

  const filled = useMemo(() => fillDays(series, win.from, win.to), [series, win]);
  const topGuides = useMemo(
    () => [...guides].sort((a, b) => b.calls - a.calls).slice(0, 8),
    [guides],
  );

  // Links into /calls keep the current traffic segment and engine so the
  // filters carry over.
  const callsHref = (extra?: Record<string, string>) => {
    const params = new URLSearchParams(extra);
    if (traffic !== "app") params.set("source", traffic);
    if (engine !== "all") params.set("engine", engine);
    const q = params.toString();
    return q ? `/calls?${q}` : "/calls";
  };

  const sentimentDelta =
    overview?.avgSentiment != null && prevOverview?.avgSentiment != null
      ? overview.avgSentiment - prevOverview.avgSentiment
      : null;

  return (
    <div className="list-page dash-page">
      <div className="list-toolbar">
        <h1>Dashboard</h1>
        <div className="spacer" />
        <TrafficToggle value={traffic} onChange={setTraffic} />
        <InputModeToggle value={mode} onChange={setMode} />
        <EngineSelect value={engine} onChange={setEngine} />
        <RangePicker value={preset} onChange={setPreset} />
      </div>

      {error && (
        <div className="callout">
          Analytics data is unavailable right now ({error}). Once the analytics service is
          reachable this dashboard fills in automatically.
        </div>
      )}

      <div className="kpi-row">
        <KpiCard
          label="Calls"
          value={overview ? String(overview.calls) : "—"}
          hint={
            "Socket connections, not conversations: the app reconnects on every background/" +
            "resume, so one conversation is usually several calls. See Conversations below."
          }
          delta={
            <Delta
              value={overview && prevOverview ? pctDelta(overview.calls, prevOverview.calls) : null}
            />
          }
        />
        <KpiCard
          label="Avg duration"
          value={overview ? formatDurationMs(overview.avgDurationMs) : "—"}
          delta={
            <Delta
              value={
                overview?.avgDurationMs != null && prevOverview?.avgDurationMs != null
                  ? pctDelta(overview.avgDurationMs, prevOverview.avgDurationMs)
                  : null
              }
            />
          }
        />
        <KpiCard
          label="Avg sentiment"
          value={overview ? formatSentiment(overview.avgSentiment) : "—"}
          delta={<Delta value={sentimentDelta} suffix="" />}
          hint={
            "How the USER was feeling in transcripts (-1 very negative … +1 very positive). " +
            "Mood context, not a quality score — a user venting about a hard day reads negative " +
            "even when the companion handled it perfectly. Judge quality by the row below."
          }
        />
        <KpiCard
          label="Flagged calls"
          value={overview ? String(overview.flaggedCount) : "—"}
          delta={
            <Delta
              invert
              value={
                overview && prevOverview
                  ? pctDelta(overview.flaggedCount, prevOverview.flaggedCount)
                  : null
              }
            />
          }
          hint="Calls the analyzer flagged: distress, crisis mention, or the companion breaking persona."
        />
        <KpiCard
          label="Live now"
          value={overview ? String(overview.liveNow) : "—"}
          hint={
            "Open call records right now. Includes voice calls cut off by crashes/deploys until " +
            "the 10-minute sweep closes them (idle text chats close after 30 min, orphaned voice " +
            "rows after 6 h), so brief spikes after a deploy are cleanup lag, not real traffic. " +
            "Ignores the input-mode filter: input mode is only known once a call ends."
          }
        />
      </div>

      {/* Companion quality — the Aug 2026 clinical-feedback metrics. These,
          not sentiment, answer "is the companion getting better per release". */}
      <div className="kpi-row">
        <KpiCard
          label="Early drops (conversations)"
          value={overview ? formatRate(overview.conversationEarlyDropRate) : "—"}
          delta={
            <Delta
              invert
              value={rateDelta(
                overview?.conversationEarlyDropRate,
                prevOverview?.conversationEarlyDropRate,
              )}
            />
          }
          hint={
            "Finished conversations with at most 2 user turns — the user left before the " +
            "conversation got going. Counted per conversation (reconnects joined), not per " +
            "socket: the old per-call 3-minute rule mostly counted reconnects. Lower is better."
          }
        />
        <KpiCard
          label="Exercise push rate"
          value={overview ? formatRate(overview.interventionRate) : "—"}
          delta={
            <Delta
              value={
                overview?.interventionRate != null && prevOverview?.interventionRate != null
                  ? pctDelta(overview.interventionRate, prevOverview.interventionRate)
                  : null
              }
            />
          }
          hint={
            "Share of analyzed calls where the companion suggested a grounding/breathing/" +
            "activity exercise. Not inherently bad — but exercises should follow exploration " +
            "and be offered as a choice."
          }
        />
        <KpiCard
          label="Disengaged after exercise"
          value={overview ? formatRate(overview.disengagedAfterInterventionRate) : "—"}
          delta={
            <Delta
              invert
              value={
                overview?.disengagedAfterInterventionRate != null &&
                prevOverview?.disengagedAfterInterventionRate != null
                  ? pctDelta(
                      overview.disengagedAfterInterventionRate,
                      prevOverview.disengagedAfterInterventionRate,
                    )
                  : null
              }
            />
          }
          hint={
            "Of calls with an exercise suggestion, how many ended within ~2 user turns of it — " +
            "the clearest 'intervened before understanding' signal. Lower is better."
          }
        />
        <KpiCard
          label="Rubric score"
          value={overview ? formatRate(overview.avgEvalScore) : "—"}
          delta={
            <Delta
              value={
                overview?.avgEvalScore != null && prevOverview?.avgEvalScore != null
                  ? pctDelta(overview.avgEvalScore, prevOverview.avgEvalScore)
                  : null
              }
            />
          }
          hint={
            "LLM-judge average across the 13-criterion companion rubric (validation, " +
            "explore-before-intervene, offering choice, …) on sampled calls. Per-version " +
            "breakdown lives on the Evals page. Higher is better."
          }
        />
        <KpiCard
          label="Avg user turns"
          value={
            overview?.avgUserMsgCount != null ? overview.avgUserMsgCount.toFixed(1) : "—"
          }
          delta={
            <Delta
              value={
                overview?.avgUserMsgCount != null && prevOverview?.avgUserMsgCount != null
                  ? pctDelta(overview.avgUserMsgCount, prevOverview.avgUserMsgCount)
                  : null
              }
            />
          }
          hint="User messages per call (per socket connection). For depth per real conversation see Median user turns below."
        />
      </div>

      {/* Conversation level (WS0): rows regrouped by conversation, plus the
          deterministic health metrics computed on every connection. */}
      <div className="kpi-section-head">
        Conversation health
        <WeightToggle value={weight} onChange={setWeight} />
      </div>
      <div className="kpi-row">
        <KpiCard
          label="Conversations"
          value={overview ? String(overview.conversations) : "—"}
          delta={
            <Delta
              value={
                overview && prevOverview
                  ? pctDelta(overview.conversations, prevOverview.conversations)
                  : null
              }
            />
          }
          hint={
            "Real conversations: connections grouped by the app's interaction id, else joined " +
            `when the user is back within 10 minutes. ${formatNumber(
              overview?.connectionsPerConversation,
            )} connections per conversation.`
          }
        />
        <KpiCard
          label="Median user turns"
          value={formatNumber(overview?.medianUserTurnsPerConversation)}
          delta={
            <Delta
              value={rateDelta(
                overview?.medianUserTurnsPerConversation,
                prevOverview?.medianUserTurnsPerConversation,
              )}
            />
          }
          hint="User messages in the median conversation. First-conversation depth predicts whether users return. Higher is better."
        />
        <KpiCard
          label="Typed share"
          value={overview ? formatRate(overview.typedShare) : "—"}
          hint={
            "Conversations where every user turn was typed (keyboard mode rides the voice " +
            "pipeline, so these used to be logged as voice calls)."
          }
        />
        <KpiCard
          label="Talk-over rate"
          value={overview ? formatRate(overview.talkOverRate) : "—"}
          delta={
            <Delta invert value={rateDelta(overview?.talkOverRate, prevOverview?.talkOverRate)} />
          }
          hint={
            "Companion replies the user cut off having heard less than 15% — the companion " +
            `talked over them. Any interruption: ${formatRate(overview?.interruptionRate)}. ` +
            "Lower is better."
          }
        />
        <KpiCard
          label="Fragmented turns"
          value={overview ? formatRate(overview.fragmentedTurnRate) : "—"}
          delta={
            <Delta
              invert
              value={rateDelta(overview?.fragmentedTurnRate, prevOverview?.fragmentedTurnRate)}
            />
          }
          hint="User messages that followed another user message with no reply in between — the user was cut into fragments. Lower is better."
        />
        <KpiCard
          label="Ends in a question"
          value={overview ? formatRate(overview.questionEndRate) : "—"}
          hint="Share of companion replies ending in “?”. Some is healthy; nearly every reply reads as an interview."
        />
        <KpiCard
          label="Advice → question"
          value={overview ? formatRate(overview.adviceDeflectionRate) : "—"}
          delta={
            <Delta
              invert
              value={rateDelta(overview?.adviceDeflectionRate, prevOverview?.adviceDeflectionRate)}
            />
          }
          hint={
            "When the user explicitly asked for advice / what to do / how to, how often the " +
            `reply ended in another question (${overview?.adviceRequests ?? 0} asks). Lower is better.`
          }
        />
        <KpiCard
          label="Reply words (voice / text)"
          value={
            overview
              ? `${formatNumber(overview.medianReplyWordsByInputMode.voice, 0)} / ${formatNumber(
                  overview.medianReplyWordsByInputMode.text,
                  0,
                )}`
              : "—"
          }
          hint="Median words per companion reply, on spoken vs typed conversations. Spoken replies should be short."
        />
      </div>

      <div className="charts-row">
        <div className="panel">
          <div className="panel-head">Calls per day</div>
          {loading ? <div className="state-box">Loading…</div> : <CallsBarChart points={filled} />}
        </div>
        <div className="panel">
          <div className="panel-head">Avg sentiment</div>
          {loading ? (
            <div className="state-box">Loading…</div>
          ) : (
            <SentimentLineChart points={filled} />
          )}
        </div>
      </div>

      <div className="dash-grid">
        <div className="panel">
          <div className="panel-head">Top companions</div>
          {loading ? (
            <div className="state-box">Loading…</div>
          ) : topGuides.length === 0 ? (
            <div className="state-box">No companion activity in this range.</div>
          ) : (
            <table className="guide-table">
              <thead>
                <tr>
                  <th className="no-sort">Companion</th>
                  <th className="no-sort">Calls</th>
                  <th className="no-sort">Users</th>
                  <th className="no-sort">Avg dur</th>
                  <th className="no-sort">Sentiment</th>
                  <th className="no-sort">Rubric</th>
                  <th className="no-sort">Last call</th>
                </tr>
              </thead>
              <tbody>
                {topGuides.map((g) => (
                  <tr
                    key={g.guideId}
                    className="guide-row"
                    onClick={() => navigate(`/guides/${encodeURIComponent(g.guideId)}?tab=insights`)}
                  >
                    <td><span className="name">{g.guideName || g.guideId}</span></td>
                    <td>{g.calls}</td>
                    <td>{g.uniqueUsers}</td>
                    <td>{formatDurationMs(g.avgDurationMs)}</td>
                    <td><SentimentCell score={g.avgSentiment} /></td>
                    {/* Rubric average over the judged subset — the count is
                        the denominator, so "83% · 5" never reads as 5 bad calls. */}
                    <td>
                      {g.evalCount === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        <>
                          <span className={`rubric-score sm ${scoreTone(g.avgEvalScore)}`}>
                            {Math.round((g.avgEvalScore ?? 0) * 100)}%
                          </span>
                          <span className="muted"> · {g.evalCount}</span>
                        </>
                      )}
                    </td>
                    <td className="voice-cell">{formatDateTime(g.lastCallAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="panel">
          <div className="panel-head">
            Recent calls
            <span className="spacer" />
            <button className="btn btn-ghost btn-sm" onClick={() => navigate(callsHref())}>
              View all →
            </button>
          </div>
          {loading ? (
            <div className="state-box">Loading…</div>
          ) : (
            <RecentCallsList
              calls={recent}
              onOpen={(c) => navigate(callsHref({ call: c.id }))}
            />
          )}
        </div>
      </div>
    </div>
  );
}
